import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraSimpleGrid,
  CheckboxV2,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  RadioV2,
  Text,
  TextInputV2,
  UniversalSelect,
  useToastMessage,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import Header from '~/components/common/Header';
import { PAGE_LAYOUT, PAGE_TOP_PADDING } from '~/lib/constants';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import { Path } from '~/routes/constants';
import { useGetProviderTypesEnumQuery } from '~/store/services/enumerations';
import {
  useAddNoteTemplateMutation,
  useGetNoteTemplatesQuery,
  useUpdateNoteTemplateMutation,
} from '~/store/services/notes/noteTemplates';
import { useGetAppointmentTypesQuery } from '~/store/services/schedule';

const TOP_LEVEL_ID = 'add-note-template';

type AddNoteTemplateFieldErrors = {
  appointmentTypeLabels?: boolean;
  providerTypes?: boolean;
  selectedTemplate?: boolean;
  templateTitle?: boolean;
};

enum TemplateMode {
  ADD,
  EDIT,
}

const ModifyNoteTemplates = () => {
  const history = useHistory();
  const { createToast } = useToastMessage();
  const hasShownToast = useRef(false);

  // rtk queries
  const { data: noteTemplatesList, isLoading: isGetNoteTemplatesLoading } =
    useGetNoteTemplatesQuery();
  const { data: providerTypesList, isLoading: isGetProviderTypesLoading } =
    useGetProviderTypesEnumQuery();
  const {
    data: appointmentTypesList,
    isLoading: isGetAppointmentTypesLoading,
  } = useGetAppointmentTypesQuery();
  const [
    addNoteTemplate,
    {
      isSuccess: isAddNoteTemplateSuccess,
      isLoading: isAddNoteTemplateLoading,
      isError: isAddNoteTemplateError,
    },
  ] = useAddNoteTemplateMutation();
  const [
    updateNoteTemplate,
    {
      isSuccess: isUpdateNoteTemplateSuccess,
      isLoading: isUpdateNoteTemplateLoading,
      isError: isUpdateNoteTemplateError,
    },
  ] = useUpdateNoteTemplateMutation();

  // state
  const [addOrEditRadio, setAddOrEditRadio] = useState<TemplateMode>(
    TemplateMode.ADD,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<NoteTemplate>(null);
  const [templateTitle, setTemplateTitle] = useState<string>();
  const [selectedProviderTypes, setSelectedProviderTypes] =
    useState<string[]>();
  const [selectedAppointmentTypes, setSelectedAppointmentTypes] =
    useState<string[]>();

  const defaultFieldErrors = {
    appointmentTypeLabels: false,
    providerTypes: false,
    selectedTemplate: false,
    templateTitle: false,
  };
  const [formFieldErrors, setFormFieldErrors] =
    useState<AddNoteTemplateFieldErrors>(defaultFieldErrors);

  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [hasAiSummary, setHasAiSummary] = useState(false);
  const [showTreatmentOverview, setShowTreatmentOverview] = useState(false);
  const [showOverdueAssessments, setShowOverdueAssessments] = useState(false);
  const [showCarePlan, setShowCarePlan] = useState(false);
  const [showCarePlanGoals, setShowCarePlanGoals] = useState(false);
  const [showCarePlanInterventions, setShowCarePlanInterventions] =
    useState(false);

  useEffect(() => {
    if (selectedTemplate) {
      setShowSessionSummary(selectedTemplate.showSessionSummary);
      setHasAiSummary(selectedTemplate.hasAiSummary);
      setShowTreatmentOverview(selectedTemplate.showTreatmentOverview);
      setShowOverdueAssessments(selectedTemplate.showOverdueAssessments);
      setShowCarePlan(selectedTemplate.showCarePlan);
      setShowCarePlanGoals(selectedTemplate.showCarePlanGoals);
      setShowCarePlanInterventions(selectedTemplate.showCarePlanInterventions);
    }
  }, [selectedTemplate]);

  // global variables
  const isAddMode = addOrEditRadio === TemplateMode.ADD;
  const isEditMode = addOrEditRadio === TemplateMode.EDIT;
  const isEditingTemplate = isEditMode && selectedTemplate !== null;
  const isShowFields = isAddMode || isEditingTemplate;

  // dropdown memos
  const providerTypesOptions = useMemo<UniversalSelectOption[]>(() => {
    return (providerTypesList ?? []).map(({ label, id }: Enumeration) => ({
      label,
      value: id,
    }));
  }, [providerTypesList]);

  const appointmentTypesOptions = useMemo<UniversalSelectOption[]>(() => {
    return (appointmentTypesList ?? []).map(
      ({ appointmentTypeLabel, appointmentTypeExtId }: AppointmentType) => ({
        label: appointmentTypeLabel,
        value: appointmentTypeExtId,
      }),
    );
  }, [appointmentTypesList]);

  // handlers
  const handleProviderTypeChange = (selected: UniversalSelectOption[]) => {
    setSelectedProviderTypes(selected?.map((option) => option.label));
  };
  const handleAppointmentTypeChange = (selected: UniversalSelectOption[]) => {
    setSelectedAppointmentTypes(selected?.map((option) => option.label));
  };
  const getMultiSelectedUniversalSelectOptions = (
    selectedValues: string[],
    options: UniversalSelectOption[],
  ): UniversalSelectOption[] => {
    return options.filter((option) => selectedValues?.includes(option.label));
  };
  const resetForm = () => {
    if (selectedTemplate) setSelectedTemplate(null);
    setSelectedAppointmentTypes([]);
    setSelectedProviderTypes([]);
    setTemplateTitle('');
    setShowSessionSummary(false);
    setHasAiSummary(false);
    setShowTreatmentOverview(false);
    setShowOverdueAssessments(false);
    setShowCarePlan(false);
    setShowCarePlanGoals(false);
    setShowCarePlanInterventions(false);
  };

  const handleTemplateMode = (action: TemplateMode) => {
    resetForm();
    setFormFieldErrors(defaultFieldErrors);
    setAddOrEditRadio(action);
  };

  const getTemplateFromSelectedOption = (
    selected: UniversalSelectOption,
  ): NoteTemplate | undefined => {
    return noteTemplatesList?.find(
      (template: NoteTemplate) => template?.templateId === selected.value,
    );
  };

  const handleTemplateSelectChange = (selected: UniversalSelectOption) => {
    const selectedTemplate: NoteTemplate =
      getTemplateFromSelectedOption(selected);
    setSelectedTemplate(selectedTemplate);
    if (selectedTemplate) {
      setSelectedProviderTypes(selectedTemplate.providerTypes);
      setSelectedAppointmentTypes(selectedTemplate.appointmentTypeLabels);
      setTemplateTitle(selectedTemplate.templateTitle);
    }
  };
  const validateForm = () => {
    setFormFieldErrors({
      appointmentTypeLabels:
        !selectedAppointmentTypes || selectedAppointmentTypes.length === 0,
      providerTypes:
        !selectedProviderTypes || selectedProviderTypes.length === 0,
      templateTitle: !templateTitle,
    });
  };
  const localeCompareSort = (a: string, b: string) => a.localeCompare(b);
  const hasProviderTypesChanged = (): boolean => {
    const originalProviderTypes = selectedTemplate?.providerTypes || [];
    return (
      JSON.stringify([...originalProviderTypes].sort(localeCompareSort)) !==
      JSON.stringify([...(selectedProviderTypes || [])].sort(localeCompareSort))
    );
  };
  const hasAppointmentTypesChanged = (): boolean => {
    const originalAppointmentTypes =
      selectedTemplate?.appointmentTypeLabels || [];
    return (
      JSON.stringify([...originalAppointmentTypes].sort(localeCompareSort)) !==
      JSON.stringify(
        [...(selectedAppointmentTypes || [])].sort(localeCompareSort),
      )
    );
  };
  const isNoteTemplateUpdateChanged =
    hasProviderTypesChanged() ||
    hasAppointmentTypesChanged() ||
    templateTitle !== selectedTemplate?.templateTitle ||
    showSessionSummary !== selectedTemplate?.showSessionSummary ||
    hasAiSummary !== selectedTemplate?.hasAiSummary ||
    showTreatmentOverview !== selectedTemplate?.showTreatmentOverview ||
    showOverdueAssessments !== selectedTemplate?.showOverdueAssessments ||
    showCarePlan !== selectedTemplate?.showCarePlan ||
    showCarePlanGoals !== selectedTemplate?.showCarePlanGoals ||
    showCarePlanInterventions !== selectedTemplate?.showCarePlanInterventions;

  const resetHasShownToastRef = () => {
    hasShownToast.current = true;
    setTimeout(() => {
      hasShownToast.current = false;
    }, 2000);
  };

  const handleSave = () => {
    validateForm();
    const isFormValid =
      selectedAppointmentTypes?.length > 0 &&
      selectedProviderTypes?.length > 0 &&
      templateTitle?.length > 0;
    if (isFormValid) {
      if (isAddMode) {
        addNoteTemplate({
          appointmentTypeLabels: selectedAppointmentTypes,
          providerTypes: selectedProviderTypes,
          templateTitle,
          showSessionSummary,
          hasAiSummary,
          showTreatmentOverview,
          showOverdueAssessments,
          showCarePlan,
          showCarePlanGoals,
          showCarePlanInterventions,
        })
          .unwrap()
          .then(() => {
            createToast({
              icon: FAIconName.checkRegular,
              id: 'add-note-template-success_toast',
              text: 'Added Note Template successfully.',
              variant: 'success',
            });
          })
          .catch(() => {
            createToast({
              icon: FAIconName.exclamationTriangleRegular,
              id: 'add-note-template-error_toast',
              text: "We're sorry, we were unable to save note template changes. Please try again",
              variant: 'critical',
            });
          });
      }
      if (isEditingTemplate && isNoteTemplateUpdateChanged) {
        updateNoteTemplate({
          appointmentTypeLabels: selectedAppointmentTypes,
          providerTypes: selectedProviderTypes,
          templateId: selectedTemplate?.templateId,
          templateTitle,
          showSessionSummary,
          hasAiSummary,
          showTreatmentOverview,
          showOverdueAssessments,
          showCarePlan,
          showCarePlanGoals,
          showCarePlanInterventions,
        })
          .unwrap()
          .then(() => {
            createToast({
              icon: FAIconName.checkRegular,
              id: 'update-note-template-success_toast',
              text: 'Updated Note Template successfully.',
              variant: 'success',
            });
            resetHasShownToastRef();
          })
          .catch(() => {
            createToast({
              icon: FAIconName.exclamationTriangleRegular,
              id: 'update-note-template-error_toast',
              text: "We're sorry, we were unable to save note template changes. Please try again",
              variant: 'critical',
            });
            resetHasShownToastRef();
          });
      }
    }
  };

  const isSaveDisabled = (): boolean => {
    const isAddWithError = isAddMode && isAddNoteTemplateError;
    const isEditWithoutChangesOrWithError =
      isEditingTemplate &&
      (!(selectedTemplate && templateTitle && isNoteTemplateUpdateChanged) ||
        isUpdateNoteTemplateError);
    return isAddWithError || isEditWithoutChangesOrWithError;
  };

  const isSaveLoading = (): boolean => {
    if (isAddMode) {
      return isAddNoteTemplateLoading;
    }
    if (isEditingTemplate) {
      return isUpdateNoteTemplateLoading;
    }
    return false;
  };

  const templateOptions = useMemo<UniversalSelectOption[]>(() => {
    return (noteTemplatesList ?? []).map(
      ({ templateTitle, templateId }: NoteTemplate) => ({
        label: templateTitle,
        value: templateId,
      }),
    );
  }, [noteTemplatesList]);

  useEffect(() => {
    if (isAddMode && isAddNoteTemplateSuccess) {
      resetForm();
    } else if (
      isEditingTemplate &&
      isNoteTemplateUpdateChanged &&
      isUpdateNoteTemplateSuccess
    ) {
      resetForm();
    }
  }, [isAddNoteTemplateSuccess, isUpdateNoteTemplateSuccess]);

  return (
    <ChakraBox
      paddingRight={PAGE_LAYOUT.rightSpacing}
      paddingTop={PAGE_LAYOUT.topSpacing}
    >
      <Header title="Note Templates" />
      <ChakraBox gap="40px" marginTop={PAGE_TOP_PADDING}>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__reset-button`}
          onClick={() => {
            resetForm();
            history.push(Path.ADMIN_MANAGE_CONTENT);
          }}
          variant="tertiary"
        >
          Back to manage content
        </FireflyButton>
      </ChakraBox>
      <ChakraBox
        borderRadius="12px"
        borderWidth="1px"
        marginTop="12px"
        padding="18px"
      >
        <ChakraFlex gap="20px" marginBottom="20px">
          <RadioV2
            id={`${TOP_LEVEL_ID}_add__radio`}
            isChecked={addOrEditRadio === TemplateMode.ADD}
            label="Add new"
            onChange={() => handleTemplateMode(TemplateMode.ADD)}
            size="lg"
          />
          <RadioV2
            id={`${TOP_LEVEL_ID}_edit__radio`}
            isChecked={isEditMode}
            label="Edit existing"
            onChange={() => handleTemplateMode(TemplateMode.EDIT)}
            size="lg"
          />
        </ChakraFlex>
        {isEditMode && (
          <InputGroupV2
            id={`${TOP_LEVEL_ID}_template__group`}
            isRequired
            label="Choose Note Template"
            shouldShowAsteriskWhenRequired
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}_template__select`}
              isClearable={false}
              isInvalid={formFieldErrors.selectedTemplate}
              isLoading={isGetNoteTemplatesLoading}
              isSingleSelect
              onChange={handleTemplateSelectChange}
              options={templateOptions}
              value={getSelectedUniversalSelectOption(
                selectedTemplate?.templateId,
                templateOptions,
              )}
            />
          </InputGroupV2>
        )}
        {isShowFields && (
          <>
            <Text marginBottom="20px" marginTop="20px" variant="h5">
              {isAddMode ? 'Adding Note Template' : 'Editing Note Template'}
            </Text>
            <InputGroupV2
              id={`${TOP_LEVEL_ID}_title__group`}
              isRequired
              label="Template Title"
              shouldHideOptionalLabel
              shouldShowAsteriskWhenRequired
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}_title__input`}
                isInvalid={formFieldErrors.templateTitle}
                onChange={setTemplateTitle}
                value={templateTitle}
              />
            </InputGroupV2>
            <ChakraSimpleGrid columns={2} spacing="24px" width="100%">
              <InputGroupV2
                id={`${TOP_LEVEL_ID}_providerTypes__group`}
                isRequired
                label="Provider Types"
                shouldHideOptionalLabel
                shouldShowAsteriskWhenRequired
              >
                <UniversalSelect
                  id={`${TOP_LEVEL_ID}_providerTypes__select`}
                  isInvalid={formFieldErrors.providerTypes}
                  isLoading={isGetProviderTypesLoading}
                  isSingleSelect={false}
                  onChange={handleProviderTypeChange}
                  options={providerTypesOptions}
                  value={getMultiSelectedUniversalSelectOptions(
                    selectedProviderTypes,
                    providerTypesOptions,
                  )}
                  withCheckBox
                />
              </InputGroupV2>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}_appointmentTypeLabels__group`}
                isRequired
                label="Appointment Type Labels"
                shouldHideOptionalLabel
                shouldShowAsteriskWhenRequired
              >
                <UniversalSelect
                  id={`${TOP_LEVEL_ID}_appointmentTypeLabels__select`}
                  isInvalid={formFieldErrors.appointmentTypeLabels}
                  isLoading={isGetAppointmentTypesLoading}
                  isSingleSelect={false}
                  onChange={handleAppointmentTypeChange}
                  options={appointmentTypesOptions}
                  value={getMultiSelectedUniversalSelectOptions(
                    selectedAppointmentTypes,
                    appointmentTypesOptions,
                  )}
                  withCheckBox
                />
              </InputGroupV2>
            </ChakraSimpleGrid>
            <ChakraFlex flexDirection="column" marginY="10px">
              <CheckboxV2
                id={`${TOP_LEVEL_ID}_showSessionSummary`}
                isChecked={showSessionSummary}
                label="Show session summary"
                onChange={setShowSessionSummary}
              />
              <CheckboxV2
                id={`${TOP_LEVEL_ID}hasAiSummary`}
                isChecked={hasAiSummary}
                label="Has AI summary"
                onChange={setHasAiSummary}
              />
              <CheckboxV2
                id={`${TOP_LEVEL_ID}_showTreatmentOverview`}
                isChecked={showTreatmentOverview}
                label="Show treatment overview"
                onChange={setShowTreatmentOverview}
              />
              <CheckboxV2
                id={`${TOP_LEVEL_ID}_show-overdue-assessments`}
                isChecked={showOverdueAssessments}
                label="Show overdue assessments"
                onChange={setShowOverdueAssessments}
              />
              <CheckboxV2
                id={`${TOP_LEVEL_ID}_show-care-plan`}
                isChecked={showCarePlan}
                label="Show care plan"
                onChange={setShowCarePlan}
              />
              <CheckboxV2
                id={`${TOP_LEVEL_ID}_show-care-plan-goals`}
                isChecked={showCarePlanGoals}
                label="Show care plan goals"
                onChange={setShowCarePlanGoals}
              />
              <CheckboxV2
                id={`${TOP_LEVEL_ID}_show-care-plan-interventions`}
                isChecked={showCarePlanInterventions}
                label="Show care plan interventions"
                onChange={setShowCarePlanInterventions}
              />
            </ChakraFlex>
            <ChakraFlex
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <FireflyButton
                id={`${TOP_LEVEL_ID}_reset__button`}
                onClick={resetForm}
                variant="secondary"
              >
                Reset
              </FireflyButton>
              <FireflyButton
                id={`${TOP_LEVEL_ID}_save__button`}
                isDisabled={isSaveDisabled()}
                isLoading={isSaveLoading()}
                leftIcon={FAIconName.checkRegular}
                onClick={handleSave}
              >
                Save
              </FireflyButton>
            </ChakraFlex>
          </>
        )}
      </ChakraBox>
    </ChakraBox>
  );
};
export default ModifyNoteTemplates;
